/// <reference lib="webworker" />

import { Event, EventBus, Variable } from "../../../bus/EventBus";
import { uuidv7 } from "uuidv7";
import { reportError, reportStarted } from "../worker-status";
import {
  AddHistoryItemApiAddHistoryItemCommand,
  addHistoryItem,
} from "@opendonationassistant/history-service";
import type { TokenDto } from "../systems";
import { log } from "./log";

const ASTRO_WEBSOCKET_URL = "wss://astro.streamelements.com";
const RECONNECT_DELAY_MS = 1000;
const API_TIMEOUT_MS = 15000;

// Endpoint comes from the build-time environment, with a fallback to the
// production gateway.
const HISTORY_API_ENDPOINT =
  process.env.REACT_APP_HISTORY_API_ENDPOINT ?? "https://api.oda.digital";

let connectedTokens: string[] = [];

// ── StreamElements WebSocket message types ──────────────────────────

interface StreamElementsWelcome {
  type: "welcome";
  data: {
    client_id: string;
  };
}

interface StreamElementsResponse {
  type: "response";
  nonce?: string;
  error?: string;
  data: {
    message: string;
  };
}

interface StreamElementsTipEvent {
  type: "message";
  topic: "channel.tips";
  room: string;
  data: {
    _id: string;
    channel: string;
    provider: string;
    approved: string;
    status: string;
    transactionId: string;
    donation: {
      user: {
        username: string;
        geo?: string;
        email?: string;
        channel?: string;
      };
      message: string;
      amount: number;
      currency: string;
      paymentMethod: string;
    };
  };
}

type StreamElementsMessage =
  | StreamElementsWelcome
  | StreamElementsResponse
  | StreamElementsTipEvent
  | { type: "reconnect"; data: { reconnect_token: string } };

/**
 * Exchange the StreamElements JWT for channel info to obtain the channel ID.
 */
async function getChannelId(jwtToken: string): Promise<string> {
  const response = await fetch(
    "https://api.streamelements.com/kappa/v2/channels/me",
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        Accept: "application/json",
      },
      signal: AbortSignal.timeout(API_TIMEOUT_MS),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to get StreamElements channel: ${response.status}`);
  }
  const json = await response.json();
  return json._id as string;
}

/**
 * Convert the StreamElements tip amount into the ODA Amount format.
 * For USD, minor = cents (e.g. $4.20 → major=4, minor=20).
 */
function toOdaAmount(
  amount: number,
  currency: string,
): { major: number; minor: number; currency: string } {
  const major = Math.floor(amount);
  const minor = Math.round((amount - major) * 100);
  return { major, minor, currency };
}

// ── WebSocket message handling ──────────────────────────────────────

function handleWebSocketMessage(
  odaToken: string,
  recipientId: string,
  seToken: string,
  raw: string,
  eventbus: EventBus,
): void {
  const message: StreamElementsMessage = JSON.parse(raw);

  switch (message.type) {
    case "welcome": {
      log("INFO",
        `StreamElements Astro connected [client_id: ${message.data.client_id}]`,
      );

      // Fetch channel ID and subscribe to tips
      getChannelId(seToken)
        .then((channelId) => {
          log("INFO", `Subscribing to channel.tips for room ${channelId}`);
          const ws = getActiveWebSocket(seToken);
          if (!ws) return;
          ws.send(
            JSON.stringify({
              type: "subscribe",
              nonce: uuidv7(),
              data: {
                topic: "channel.tips",
                room: channelId,
                token: seToken,
                token_type: "jwt",
              },
            }),
          );
        })
        .catch((err) => {
          log("ERROR", "Failed to get StreamElements channel:", err);
          reportError(odaToken, "StreamElements", `failed to get channel: ${err}`);
        });
      break;
    }

    case "response": {
      if (message.error) {
        log("ERROR",
          `StreamElements subscribe error: ${message.error} - ${message.data.message}`,
        );
        reportError(odaToken, "StreamElements", `subscribe error: ${message.error} - ${message.data.message}`);
      } else {
        log("INFO",
          `StreamElements subscription success: ${message.data.message}`,
        );
      }
      break;
    }

    case "message": {
      if (message.topic === "channel.tips") {
        handleTipEvent(odaToken, recipientId, message, eventbus);
      }
      break;
    }

    case "reconnect": {
      log("INFO", "StreamElements requesting reconnect");
      break;
    }
  }
}

function handleTipEvent(
  odaToken: string,
  recipientId: string,
  event: StreamElementsTipEvent,
  eventbus: EventBus,
): void {
  const tip = event.data.donation;
  log("INFO",
    `StreamElements tip: ${tip.amount} ${tip.currency} from ${tip.user.username}`,
  );

  // ── 1. Persist to HistoryService as a payment event ──────────────
  const command: AddHistoryItemApiAddHistoryItemCommand = {
    nickname: tip.user.username,
    recipientId,
    amount: toOdaAmount(tip.amount, tip.currency),
    message: tip.message ?? "",
    system: "StreamElements",
    externalId: event.data._id,
    event: "payment",
    authorizationTimestamp: new Date().toISOString(),
  };

  addHistoryItem({
    baseURL: HISTORY_API_ENDPOINT,
    body: command,
    headers: {
      Authorization: `Bearer ${odaToken}`,
    },
  })
    .then(() =>
      log("INFO",
        `StreamElements tip persisted to history [${tip.amount} ${tip.currency}]`,
      ),
    )
    .catch((err) => {
      log("ERROR", "Failed to persist StreamElements tip to history:", err);
      reportError(odaToken, "StreamElements", `failed to persist tip to history: ${err}`);
    });

  // ── 2. Push a local event to the EventBus for real-time UI ───────
  const variables: Variable[] = [
    { id: uuidv7(), name: "amount", value: tip.amount, type: "number" },
    { id: uuidv7(), name: "currency", value: tip.currency, type: "string" },
    {
      id: uuidv7(),
      name: "nickname",
      value: tip.user.username,
      type: "string",
    },
    {
      id: uuidv7(),
      name: "message",
      value: tip.message ?? "",
      type: "string",
    },
    {
      id: uuidv7(),
      name: "system",
      value: "StreamElements",
      type: "string",
    },
    { id: uuidv7(), name: "externalId", value: event.data._id, type: "string" },
  ];
  eventbus.push(new Event("payment", variables));
}

// ── WebSocket lifecycle ─────────────────────────────────────────────

const activeSockets = new Map<string, WebSocket>();

function getActiveWebSocket(jwtToken: string): WebSocket | undefined {
  return activeSockets.get(jwtToken);
}

function startWebSocketClient(
  odaToken: string,
  recipientId: string,
  seToken: string,
  eventbus: EventBus,
): WebSocket {
  log("INFO", "Starting StreamElements Astro WebSocket connection");
  const websocketClient = new WebSocket(ASTRO_WEBSOCKET_URL);
  let reconnecting = false;
  activeSockets.set(seToken, websocketClient);

  // Reconnect on abnormal close/error. Guarded so error + close firing
  // together only schedule one reconnect.
  const scheduleReconnect = (): void => {
    if (reconnecting) return;
    reconnecting = true;
    setTimeout(() => {
      startWebSocketClient(odaToken, recipientId, seToken, eventbus);
    }, RECONNECT_DELAY_MS);
  };

  websocketClient.addEventListener("error", (err) => {
    log("ERROR", "StreamElements WebSocket error:", err);
    scheduleReconnect();
  });

  websocketClient.addEventListener("open", () => {
    log("INFO", "StreamElements Astro WebSocket opened");
    reportStarted(odaToken, "StreamElements");
  });

  websocketClient.addEventListener("message", (event) => {
    handleWebSocketMessage(odaToken, recipientId, seToken, event.data as string, eventbus);
  });

  websocketClient.addEventListener("close", (event) => {
    // Only remove this socket — a reconnect may have already replaced it.
    const wasRegistered = activeSockets.get(seToken) === websocketClient;
    if (wasRegistered) {
      activeSockets.delete(seToken);
    }
    if (event.code === 1000) return;
    reportError(
      odaToken,
      "StreamElements",
      `WebSocket closed with code ${event.code}${event.reason ? `: ${event.reason}` : ""}`,
    );
    if (!wasRegistered) return; // Closed by deregister or replaced — do not reconnect.
    log("INFO",
      `StreamElements Astro WebSocket closed. Reconnection attempt in ${RECONNECT_DELAY_MS}ms`,
    );
    scheduleReconnect();
  });

  return websocketClient;
}

// ── Registration (called from logger-worker) ────────────────────────

export function register(
  odaToken: string,
  recipientId: string,
  eventbus: EventBus,
  tokens: TokenDto[] | null,
): void {
  if (!tokens) {
    reportError(odaToken, "StreamElements", "Failed to fetch recipient tokens");
    return;
  }
  tokens
    .filter((t) => t.system === "StreamElements")
    .filter((t) => !connectedTokens.includes(t.id))
    .forEach((t) => {
      log("INFO", `add streamelements handler for ${t.id}`);
      connectedTokens.push(t.id);

      startWebSocketClient(odaToken, recipientId, t.token, eventbus);
    });
}

export function deregister(): void {
  log("INFO", { connected: connectedTokens }, "remove streamelements listener");
  activeSockets.forEach((websocketClient) => {
    websocketClient.close(1000, "deregistered");
  });
  activeSockets.clear();
  connectedTokens = [];
}
